import { Component, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { interval, takeWhile } from 'rxjs';

import { AssistantRunRef } from '@core/models/assistant/assistant.model';
import { JobStatusUtils, JobStatusV4 } from '@core/models/optimization.model';
import { AssistantRunsService } from '@core/services/assistant/assistant-runs.service';
import { AssistantService } from '@core/services/assistant/assistant.service';
import { ICONS } from '@core/services/icons/icons.service';
import { formatDurationAsHms } from '@core/utils/time-utils';

import { OptimizationJobStatusComponent } from '@shared/components/optimization-job-status/optimization-job-status.component';

@Component({
  selector: 'app-assistant-run-card',
  imports: [TranslatePipe, OptimizationJobStatusComponent],
  templateUrl: './assistant-run-card.component.html',
  styles: ``
})
export class AssistantRunCardComponent {
  readonly run = input.required<AssistantRunRef>();
  readonly parameterId = input<string | null>(null);
  protected readonly assistant = inject(AssistantService);
  private readonly runs = inject(AssistantRunsService);
  private readonly translate = inject(TranslateService);
  protected readonly ICONS = ICONS;
  protected readonly JobStatusV4 = JobStatusV4;
  private readonly now = signal(Date.now());

  protected readonly state = computed(() => this.runs.state(this.run().optimizationId));
  protected readonly status = computed(() => this.state()?.status ?? JobStatusV4.CREATED);
  protected readonly finished = computed(() =>
    JobStatusUtils.isTerminalStatus(this.status())
  );
  protected readonly failed = computed(
    () =>
      JobStatusUtils.isErrorStatus(this.status()) ||
      JobStatusUtils.isBlockedStatus(this.status())
  );
  protected readonly stale = computed(
    () => this.runs.disconnectedSince() !== null && !this.finished()
  );
  protected readonly staleSince = computed(() =>
    new Date(this.runs.disconnectedSince() ?? 0).toLocaleTimeString()
  );
  protected readonly shortPlan = computed(() => `${this.run().planId.slice(0, 8)}…`);
  // The socket can report the end before the assistant's stream announces the start.
  protected readonly elapsed = computed(() => {
    const endedAt = this.state()?.endedAt;
    if (this.finished() && endedAt === undefined) return null;
    return formatDurationAsHms({
      totalMilliseconds: Math.max(0, (endedAt ?? this.now()) - this.run().startedAt)
    });
  });

  constructor() {
    // A one-second clock only while the run is going, for the elapsed time.
    interval(1000)
      .pipe(
        takeWhile(() => !this.finished()),
        takeUntilDestroyed()
      )
      .subscribe(() => this.now.set(Date.now()));
  }

  protected planLink(planId: string | undefined): string | null {
    const planningSpaceId = this.run().planningSpaceId;
    const dataSpaceId = this.assistant.dataSpaceId();
    return planId && planningSpaceId && dataSpaceId !== null
      ? `/ds/${dataSpaceId}/planning-spaces/${planningSpaceId}/plan/${planId}`
      : null;
  }

  protected analyse(): void {
    void this.assistant.send(
      this.translate.instant('assistant.runs.analyse_message', {
        plan_id: this.run().planId,
        output_plan_id: this.state()?.outputPlanId ?? ''
      })
    );
  }

  protected askWhy(): void {
    void this.assistant.send(
      this.translate.instant('assistant.runs.ask_why_message', {
        plan_id: this.run().planId
      })
    );
  }
}
